import {
  Capability,
  OrgCapability,
  SectionCapability,
  UserRoleType,
  getCourseCapabilities,
  getOrgCapabilities,
  getPageCapabilities,
  getSectionCapabilities,
  getUserRole,
  hasOrgCapability,
  hasSectionCapability,
} from "../../shared/capabilities";
import { CourseRole, OrgRole, SectionRole } from "../../shared/types/roles";

import {
  courseUserSelectors,
  createAppSelector,
  orgUserSelectors,
  pageSelectors,
  sectionUserSelectors,
  selectCurrentSectionId,
  selectCurrentUserId,
  userSelectors,
} from "./base";
import {
  selectCourseGroupingUsersForCurrentCourse,
  selectCurrentUserIsCourseGroupingAdminAnywhere,
} from "./course-grouping-user";
import {
  selectCourseRoleByCourseId,
  selectCourseRoleBySectionId,
  selectCourseRoleByUserId,
  selectCurrentCourseRole,
} from "./course-user";
import { selectCurrentOrgRole, selectOrgRoleByUserId } from "./org";
import { selectPageGroupForReadyUser } from "./page-groups";
import {
  selectCurrentSectionRole,
  selectEnabledSectionUsers,
  selectSectionRoleBySectionId,
  selectSectionRoleByUserId,
} from "./section-user";
import { selectIsStudentPreview } from "./view";

import type { SectionUserType, UserType } from "components/server-types";
import { HomeRole } from "utils/urls";

export const selectUserIsMpAdmin = createAppSelector(
  [(state, userId: string) => userId, (state) => state.users],
  (userId, users) => users[userId]?.is_superuser,
);

export const selectCurrentUserIsMPAdmin = createAppSelector(
  [selectCurrentUserId, (state) => state.users, selectIsStudentPreview],
  (userId, users, isStudentPreview) => Boolean(!isStudentPreview && users[userId!]?.is_superuser),
);

// Note: using this selector is discouraged, use capability selectors instead
export const selectCurrentUserRole = createAppSelector(
  [
    selectCurrentUserIsMPAdmin,
    selectCurrentSectionRole,
    selectCurrentOrgRole,
    selectCurrentCourseRole,
    selectIsStudentPreview,
  ],
  (isMpAdmin, sectionRole, orgRole, courseRole, isStudentPreview): UserRoleType =>
    getUserRole({
      isMpAdmin,
      sectionRole,
      orgRole,
      courseRole,
      isStudentPreview,
    }),
);

export const selectCurrentUserOrgRole = createAppSelector(
  [
    selectCurrentUserIsMPAdmin,
    selectCurrentOrgRole,
    selectCurrentCourseRole,
    selectIsStudentPreview,
  ],
  (isMpAdmin, orgRole, courseRole, isStudentPreview): UserRoleType =>
    getUserRole({
      isMpAdmin,
      orgRole,
      courseRole,
      isStudentPreview,
    }),
);

export const selectUserRoleByUserId = createAppSelector(
  [selectUserIsMpAdmin, selectSectionRoleByUserId, selectOrgRoleByUserId, selectCourseRoleByUserId],
  (isMpAdmin, sectionRole, orgRole, courseRole): UserRoleType =>
    getUserRole({
      isMpAdmin,
      sectionRole,
      orgRole,
      courseRole,
    }),
);

export const selectCurrentUserRoleBySectionId = createAppSelector(
  [
    selectCurrentUserIsMPAdmin,
    selectSectionRoleBySectionId,
    selectCurrentOrgRole,
    selectCourseRoleBySectionId,
  ],
  (isMpAdmin, sectionRole, orgRole, courseRole): UserRoleType =>
    getUserRole({
      isMpAdmin,
      sectionRole,
      orgRole,
      courseRole,
    }),
);

export const selectSectionCapabilitiesBySectionId = createAppSelector(
  [selectCurrentUserRoleBySectionId],
  (userRole) => getSectionCapabilities(userRole),
);

export const selectSectionCapabilities = createAppSelector([selectCurrentUserRole], (userRole) =>
  getSectionCapabilities(userRole),
);

export const selectCourseCapabilities = createAppSelector(
  [
    selectCurrentUserIsMPAdmin,
    selectCurrentOrgRole,
    selectCurrentCourseRole,
    selectIsStudentPreview,
  ],
  (isMpAdmin, orgRole, courseRole, isStudentPreview) =>
    getCourseCapabilities(
      getUserRole({
        isMpAdmin,
        orgRole,
        courseRole,
        isStudentPreview,
      }),
    ),
);

export const selectOrgCapabilities = createAppSelector([selectCurrentUserOrgRole], (userOrgRole) =>
  getOrgCapabilities(userOrgRole),
);

// The current user's effective role for a specific course. Mirrors the server's
// get_user_role_in_course.
export const selectCurrentUserRoleByCourseId = createAppSelector(
  [
    selectCurrentUserIsMPAdmin,
    selectCurrentOrgRole,
    selectCourseRoleByCourseId,
    selectIsStudentPreview,
  ],
  (isMpAdmin, orgRole, courseRole, isStudentPreview): UserRoleType =>
    getUserRole({
      isMpAdmin,
      orgRole,
      courseRole,
      isStudentPreview,
    }),
);

// All course-level capabilities for the current user in a specific course.
export const selectCourseCapabilitiesByCourseId = createAppSelector(
  [selectCurrentUserRoleByCourseId],
  (userCourseRole) => getCourseCapabilities(userCourseRole),
);

export const selectPageCapabilities = createAppSelector(
  [pageSelectors.selectById, selectCurrentUserRole, selectPageGroupForReadyUser],
  (page, currentUserRole, readyPageGroup) =>
    getPageCapabilities(page, currentUserRole, Boolean(readyPageGroup)),
);

export const selectSectionCapabilitiesByUserId = createAppSelector(
  [selectUserRoleByUserId],
  (userRole) => getSectionCapabilities(userRole),
);

export const selectIsRecordingAgent = createAppSelector(
  [selectCurrentUserRole],
  (userRole) => userRole === UserRoleType.RECORDING_AGENT,
);

const getSectionUsersWithSectionCapability = (
  sectionUsers: SectionUserType[],
  capability: SectionCapability,
  sectionId: string | null,
) => {
  if (!sectionId) {
    return [];
  }
  return sectionUsers.filter((sectionUser) => {
    if (sectionUser.section_id !== sectionId) {
      return false;
    }
    const sectionRole = sectionUser.role;
    return hasSectionCapability(
      getUserRole({
        isMpAdmin: false,
        sectionRole,
      }),
      capability,
    );
  });
};

const getSectionUserIdsWithSectionCapability = (
  sectionUsers: SectionUserType[],
  capability: SectionCapability,
  sectionId: string | null,
) => getSectionUsersWithSectionCapability(sectionUsers, capability, sectionId).map(({ id }) => id);

export const selectSectionUserIdsWithSectionCapability = createAppSelector(
  [
    selectEnabledSectionUsers,
    selectCurrentSectionId,
    (state, capability: SectionCapability) => capability,
  ],
  (sectionUsers, sectionId, capability) =>
    getSectionUserIdsWithSectionCapability(sectionUsers, capability, sectionId),
);

// User role map in the current section
// This is used to determine who has what capabilities in the current section.
// TODO: consider using selectEnabledSectionUsers for this
export const selectUserRoleMapInCurrentSection = createAppSelector(
  [
    userSelectors.selectAll,
    sectionUserSelectors.filterForCurrentSection,
    orgUserSelectors.selectAll,
    courseUserSelectors.filterForCurrentCourse,
    selectCourseGroupingUsersForCurrentCourse,
  ],
  (users, sectionUsers, orgUsers, courseUsers, groupingUsers): Array<[UserType, UserRoleType]> => {
    // Users with a role in the section include:
    // - Section users with a role in the section
    // - Org users with a role in the org other than "none"
    // - Course admins with a CourseUser or CourseGroupingUser row for the current section's course
    // - Users with a system-wide role
    // but does not include disabled users at the section or org level

    const sectionUsersByUserId = new Map(
      sectionUsers.map((sectionUser) => [sectionUser.user_id, sectionUser]),
    );

    const orgUsersByUserId = new Map(orgUsers.map((ou) => [ou.user_id, ou]));
    const courseUsersByUserId = new Map(courseUsers.map((cu) => [cu.user_id, cu]));
    const groupingUsersByUserId = new Map(groupingUsers.map((gu) => [gu.user_id, gu]));

    const enabledUsers = users.filter((user) => {
      const sectionUser = sectionUsersByUserId.get(user.id);
      if (sectionUser && sectionUser.is_disabled) {
        return false;
      }

      const orgUser = orgUsersByUserId.get(user.id);
      if (orgUser && orgUser.is_disabled && orgUser.role !== OrgRole.NONE) {
        return false;
      }

      return true;
    });

    return enabledUsers.map((user) => [
      user,
      getUserRole({
        isMpAdmin: user.is_superuser,
        sectionRole: sectionUsersByUserId.get(user.id)?.role || null,
        orgRole: orgUsersByUserId.get(user.id)?.role || null,
        courseRole:
          courseUsersByUserId.get(user.id)?.role ||
          groupingUsersByUserId.get(user.id)?.role ||
          null,
      }),
    ]);
  },
);

// User with specific capabilities in the current section

const getUsersWithSectionCapability = (
  userRoleMap: Array<[UserType, UserRoleType]>,
  capability: SectionCapability,
) => {
  return userRoleMap
    .filter(([, role]) => hasSectionCapability(role, capability))
    .map(([user]) => user);
};

export const selectUsersWhoCanParticipateInSection = createAppSelector(
  [selectUserRoleMapInCurrentSection],
  (userRoleMap) =>
    getUsersWithSectionCapability(userRoleMap, Capability.CAN_PARTICIPATE_IN_SECTION),
);

// User IDs with specific capabilities in the current section

const getUserIdsWithSectionCapability = (
  userRoleMap: Array<[UserType, UserRoleType]>,
  capability: SectionCapability,
) => {
  return getUsersWithSectionCapability(userRoleMap, capability).map((user) => user.id);
};

export const selectUserIdsWhoAreVisibleInClass = createAppSelector(
  [selectUserRoleMapInCurrentSection],
  (userRoleMap) => getUserIdsWithSectionCapability(userRoleMap, Capability.IS_VISIBLE_IN_CLASS),
);

export const selectUserIdsWhoCanAuthorSection = createAppSelector(
  [selectUserRoleMapInCurrentSection],
  (userRoleMap) => getUserIdsWithSectionCapability(userRoleMap, Capability.CAN_AUTHOR_SECTION),
);

export const selectUserIdsWhoCanCreateChatMessage = createAppSelector(
  [selectUserRoleMapInCurrentSection],
  (userRoleMap) => getUserIdsWithSectionCapability(userRoleMap, Capability.CAN_CREATE_CHAT_MESSAGE),
);

export const selectUserIdsWhoCanParticipateInSection = createAppSelector(
  [selectUserRoleMapInCurrentSection, selectEnabledSectionUsers],
  (userRoleMap, enabledSectionUsers) => {
    const studentUserIds = getUserIdsWithSectionCapability(
      userRoleMap,
      Capability.CAN_PARTICIPATE_IN_SECTION,
    );

    // Filter out disabled section users
    return studentUserIds.filter((user_id) =>
      enabledSectionUsers.some((cu) => cu.user_id === user_id),
    );
  },
);

// User counts with specific capabilities in the current section

export const selectCountUsersWhoCanParticipateInSection = createAppSelector(
  [selectUserIdsWhoCanParticipateInSection],
  (userIds) => userIds.length,
);

// Home-role availability selectors.
//
// These are intentionally split into atomic boolean selectors so that
// `selectAvailableHomeRoles` only recomputes when the actual rolechanges.
const selectCurrentUserCanAuthorOrg = createAppSelector([selectCurrentUserOrgRole], (orgRole) =>
  hasOrgCapability(orgRole, OrgCapability.CAN_AUTHOR_ORG),
);

export const selectCurrentUserIsCourseAdminInAnyCourse = createAppSelector(
  [courseUserSelectors.filterForCurrentUser, selectCurrentUserIsCourseGroupingAdminAnywhere],
  (courseUsers, isGroupingCourseAdmin) =>
    courseUsers.some((cu) => cu.role === CourseRole.COURSE_ADMIN) || isGroupingCourseAdmin,
);

const selectCurrentUserIsInstructorInAnySection = createAppSelector(
  [sectionUserSelectors.filterForCurrentUser],
  (sectionUsers) => sectionUsers.some((su) => su.role === SectionRole.INSTRUCTOR),
);

const selectCurrentUserIsSectionAssistantInAnySection = createAppSelector(
  [sectionUserSelectors.filterForCurrentUser],
  (sectionUsers) => sectionUsers.some((su) => su.role === SectionRole.SECTION_ASSISTANT),
);

const selectCurrentUserIsStudentInAnySection = createAppSelector(
  [sectionUserSelectors.filterForCurrentUser],
  (sectionUsers) => sectionUsers.some((su) => su.role === SectionRole.STUDENT),
);

export const selectAvailableHomeRoles = createAppSelector(
  [
    selectCurrentUserCanAuthorOrg,
    selectCurrentUserIsCourseAdminInAnyCourse,
    selectCurrentUserIsInstructorInAnySection,
    selectCurrentUserIsSectionAssistantInAnySection,
    selectCurrentUserIsStudentInAnySection,
  ],
  (canAuthorOrg, isCourseAdmin, isInstructor, isSectionAssistant, isStudent): HomeRole[] =>
    [
      (canAuthorOrg || isCourseAdmin) && HomeRole.ADMIN,
      isInstructor && HomeRole.INSTRUCTOR,
      isSectionAssistant && HomeRole.SECTION_ASSISTANT,
      isStudent && HomeRole.STUDENT,
    ].filter(Boolean) as HomeRole[],
);
